import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { Banner } from "@moe/oss/ui/banner";
import { FormControl, FormField, FormItem } from "@moe/oss/ui/form";
import { supportedImageExtsWithDot } from "@moe/oss/utils/utils";
import { config } from "@moe/priv/config";
import { ImageCropModal } from "@web/components/ImageCropModal";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { CreateData, UpdateData } from "./schema";

export function ImageFields() {
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const form = useFormContext<CreateData | UpdateData>();

  // Subscribe only to the display fields we care about instead of the whole form object.
  const bannerDisplay = useWatch({ control: form.control, name: "bannerDisplay" });
  const avatarDisplay = useWatch({ control: form.control, name: "avatarDisplay" });
  const [cropImage, setCropImage] = useState<{ file: File; type: "avatar" | "banner" } | null>(null);
  const { createModal, closeModal } = useAppContext();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: "avatar" | "banner") => {
    const file = event.target.files?.[0];
    if (!file) return;
    const minWidth = config[fieldName].stored.minWidth;
    const minHeight = config[fieldName].stored.minHeight;
    const aspect = config[fieldName].stored.aspect;

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      console.log({ width, height });
      if (width < minWidth || height < minHeight) {
        toast.error(`Image must be at least ${minWidth}x${minHeight} pixels`);
        return;
      }
      setCropImage({ file, type: fieldName });
      createModal({
        content: (
          <ImageCropModal
            minWidth={minWidth}
            minHeight={minHeight}
            src={URL.createObjectURL(file)}
            aspect={aspect}
            onCropComplete={(blob) => handleCropComplete(blob, fieldName)}
            onClose={() => closeModal()}
          />
        ),
        onInteractOutside: (e) => {
          e.preventDefault();
        },
        // Disable dragging with the modal sheet's content because it conflicts
        // with the image cropper controls
        headerDragOnly: true
      });
    };

    img.onerror = () => {
      toast(`Failed to load image.`);
    };

    event.target.value = "";
  };

  const handleCropComplete = (blob: Blob, fieldName: "avatar" | "banner") => {
    const file = new File([blob], cropImage?.file.name || `${fieldName}.jpg`, { type: "image/jpeg" });
    form.setValue(`${fieldName}` as const, file);
    const imageUrl = URL.createObjectURL(blob);
    if (fieldName === "avatar") {
      form.setValue(`avatarDisplay` as const, imageUrl);
    } else {
      form.setValue(`bannerDisplay` as const, imageUrl);
    }
    closeModal();
  };

  return (
    <div className="relative mb-12 shrink-0">
      <FormField
        control={form.control}
        name="banner"
        render={({ field: { ref, onChange, value, ...field } }) => (
          <FormItem>
            <FormControl>
              <div
                className={`${!bannerDisplay && "opacity-75"} bg-action-tertiary flex h-48 w-full cursor-pointer items-center justify-center overflow-hidden`}
                onClick={() => bannerInputRef.current?.click()}
              >
                {bannerDisplay ? (
                  <Banner src={bannerDisplay} className="h-48" />
                ) : (
                  <PencilSquareIcon className="text-tx-primary absolute h-12 w-12" />
                )}
                <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-200 hover:opacity-10"></div>
                <input
                  type="file"
                  className="hidden"
                  ref={(node) => {
                    ref(node);
                    bannerInputRef.current = node;
                  }}
                  onChange={(e) => handleImageChange(e, "banner")}
                  accept={supportedImageExtsWithDot.join(",")}
                  {...field}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="avatar"
        render={({ field: { ref, onChange, value, ...field } }) => (
          <FormItem>
            <FormControl>
              <div
                className="bg-action-tertiary absolute -bottom-12 left-4 flex h-28 w-28 cursor-pointer items-center justify-center overflow-hidden rounded-full"
                onClick={() => avatarInputRef.current?.click()}
              >
                {avatarDisplay ? (
                  <Avatar src={avatarDisplay} className="size-28" />
                ) : (
                  <PencilSquareIcon className="text-tx-primary absolute h-8 w-8 shadow-md" />
                )}
                <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-200 hover:opacity-10"></div>
                <input
                  type="file"
                  className="hidden"
                  ref={(node) => {
                    ref(node);
                    avatarInputRef.current = node;
                  }}
                  onChange={(e) => handleImageChange(e, "avatar")}
                  accept={supportedImageExtsWithDot.join(",")}
                  {...field}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
